/**
 * Storable mixin.
 *
 * Defines some common properties and methods for all components that needs to register a store
 * module on the fly.
 *
 * The component that implements this mixin will have available the following properties:
 * - `storeName`: The component store module's name. This computed property must be overrided on
 *                the component, returning the desired store module's name.
 * - `state`: The local state for the component's store.
 * - `getters`: The local getters for the component's store.
 */
export default {
  computed: {
    /**
     * The store module's name as an string.
     * @returns {string}
     */
    storeName() {
      return Array.isArray(this.storePath) ? this.storePath.join('/') : this.storePath;
    },

    /**
     * The store module's path.
     * @returns {[]|string}
     */
    storePath() {
      throw new Error('You must override `this.storePath` to register the store module properly');
    },

    /**
     * Quick access to the module local state.
     * @returns {*}
     */
    state() {
      if (Array.isArray(this.storePath)) {
        return this.storePath.reduce((store, path) => store[path] || {}, this.$store.state);
      }
      return this.$store.state[this.storeName];
    },

    /**
     * Quick access to the module local getters.
     * @returns {{}}
     */
    getters() {
      // We use a ES6 Proxy to return the properties from our local getters.
      // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy
      return new Proxy(this.$store.getters, {
        get: (getters, name) => {
          const getter = `${this.storeName.toString()}/${name.toString()}`;
          return getters[getter];
        },
      });
    },
  },
  methods: {
    /**
     * Dispatches a module action.
     * @param {string} action
     * @param {*} payload
     * @returns {Promise<any>}
     */
    dispatch(action, payload = undefined) {
      return this.$store.dispatch(`${this.storeName}/${action}`, payload);
    },
  },
};
